import api from "../axios";
import Api from "../Api";

/* ============================================================
   NOTE ON THE URLs

   `api` is already created with `baseURL: Api.request.URL`, but every
   call below still prefixes the full absolute URL. Axios ignores
   baseURL when the URL is absolute, so this works -- the baseURL is
   simply doing nothing.

   Cleaner would be `api.post("/invoice", data)` and let baseURL
   handle the host. Left as-is here so nothing changes behaviour;
   worth a separate pass.
============================================================ */

/**
 * Shared error shape.
 *
 * The backend always answers with { S, success, errors }. When the
 * request never reaches it (network down, timeout, CORS), there is no
 * `error.response`, so we build the same shape by hand. That way the
 * caller only ever has to check `result.S`.
 */
const toError = (error, fallback) => {
  console.error(fallback, error);

  return (
    error?.response?.data || {
      S: false,
      success: false,
      errors: error?.message || fallback
    }
  );
};

// ============================================================
// CREATE INDIVIDUAL INVOICE
// POST /invoice
// ============================================================

export async function InvoiceCreate(data) {
  try {
    const response = await api.post(`${Api.request.URL}/invoice`, data);
    return response.data;
  } catch (error) {
    return toError(error, "Unable to create invoice.");
  }
}

// ============================================================
// CREATE BULK INVOICES
// POST /invoice/bulk
//
// Heads up: the backend has no rollback between invoices. If #3 of 10
// fails, #1 and #2 are already saved. Read `created_count` and `data`
// from the error response before letting the user retry -- otherwise
// they will create duplicates.
// ============================================================

export async function InvoiceBulkCreate(data) {
  try {
    const response = await api.post(`${Api.request.URL}/invoice/bulk`, data);
    return response.data;
  } catch (error) {
    return toError(error, "Unable to create invoices.");
  }
}

// ============================================================
// FIND ALL INVOICES BY AUCTION
// GET /invoice/auction/:auctionId?search=
// ============================================================

export async function InvoiceFindAll(auctionId, search = "") {
  try {
    // encodeURIComponent is required. Without it, a search containing
    // "&", "#" or "+" silently breaks the query string -- and an
    // undefined `search` was being sent as the literal text
    // "undefined", which matched nothing and looked like an empty table.
    const query = encodeURIComponent(search ?? "");

    const response = await api.get(
      `${Api.request.URL}/invoice/auction/${auctionId}?search=${query}`
    );

    return response.data;
  } catch (error) {
    return toError(error, "Unable to load invoices.");
  }
}

// ============================================================
// FIND SINGLE INVOICE
// GET /invoice/:invoiceId
// ============================================================

export async function InvoiceFindOne(invoiceId) {
  try {
    const response = await api.get(`${Api.request.URL}/invoice/${invoiceId}`);
    console.log(response)
    return response.data;
  } catch (error) {
    return toError(error, "Unable to load invoice.");
  }
}

// ============================================================
// REMOVE LOTS FROM INVOICE
// PUT /invoice/:invoiceId/remove-lots
//
//   InvoiceRemoveLots(12, ["101", "102"])
//
// The backend recalculates total, balance and payment status from the
// remaining rows, and returns the new figures. Use the response to
// refresh the UI instead of computing the new total on the client --
// they can drift apart.
//
// It will refuse in two cases, both with a readable message:
//   - removing every lot (cancel the invoice instead)
//   - the new total would fall below what has already been paid
// ============================================================

export async function InvoiceRemoveLots(invoiceId, lotNumbers) {
  try {
    const lots = Array.isArray(lotNumbers) ? lotNumbers : [lotNumbers];

    const response = await api.put(
      `${Api.request.URL}/invoice/${invoiceId}/remove-lots`,
      { lotNumbers: lots }
    );

    return response.data;
  } catch (error) {
    return toError(error, "Unable to remove lots from the invoice.");
  }
}

// ============================================================
// MARK INVOICE AS SENT
// PUT /invoice/:invoiceId/sent
// ============================================================

export async function InvoiceMarkAsSent(invoiceId) {
  try {
    // `{}`, not `[]`. The old code sent an empty array as the body,
    // so `req.body` arrived as an array on the server. Nothing read it
    // yet, but any `const { x } = req.body` added later would be a
    // confusing bug to chase.
    const response = await api.put(
      `${Api.request.URL}/invoice/${invoiceId}/sent`,
      {}
    );

    return response.data;
  } catch (error) {
    return toError(error, "Unable to mark the invoice as sent.");
  }
}

// ============================================================
// CANCEL INVOICE
// PUT /invoice/:invoiceId/cancel
//
// `reason` is optional. The backend now refuses to cancel an invoice
// that already has payments against it -- the message tells the user
// how much was paid.
// ============================================================

export async function InvoiceCancel(invoiceId, reason = null) {
  try {
    const response = await api.put(
      `${Api.request.URL}/invoice/${invoiceId}/cancel`,
      { reason }
    );

    return response.data;
  } catch (error) {
    return toError(error, "Unable to cancel the invoice.");
  }
}

// ============================================================
// SEND INDIVIDUAL INVOICE
// PUT /invoice/:invoiceId/send
// ============================================================

export async function InvoiceSend(invoiceId, files) {
  try {
    const response = await api.put(
      `${Api.request.URL}/invoice/${invoiceId}/send`,
      { files }
    );

    return response.data;
  } catch (error) {
    return toError(error, "Unable to send invoice.");
  }
}

// ============================================================
// SEND ALL INVOICES
// POST /invoice/send-all
//
// FIXED: this used to point at "/invoices/send-all/..." -- plural.
// The route is "/invoice", singular, so every call 404'd.
//
// The auctionId is no longer in the path either. The controller never
// read it; it works entirely off the `invoices` array in the body.
// The parameter is kept in the signature so existing callers do not
// break, but it is not sent.
// ============================================================

export async function InvoiceSendAll(auctionId, invoices) {
  try {
    const response = await api.post(`${Api.request.URL}/invoice/send-all`, {
      invoices
    });

    return response.data;
  } catch (error) {
    return toError(error, "Unable to send invoices.");
  }
}

// ============================================================
// ADD PAYMENT
// POST /invoice/:invoiceId/payment
// ============================================================

export async function InvoiceAddPayment(
  invoiceId,
  { amount, paymentMethod, referenceNo, remarks, paymentDate } = {}
) {
  try {
    const response = await api.post(
      `${Api.request.URL}/invoice/${invoiceId}/payment`,
      { amount, paymentMethod, referenceNo, remarks, paymentDate }
    );

    return response.data;
  } catch (error) {
    return toError(error, "Unable to add payment.");
  }
}

// ============================================================
// GET PAYMENT HISTORY
// GET /invoice/:invoiceId/payments
// ============================================================

export async function InvoiceGetPayments(invoiceId) {
  try {
    const response = await api.get(
      `${Api.request.URL}/invoice/${invoiceId}/payments`
    );

    return response.data;
  } catch (error) {
    return toError(error, "Unable to get payment history.");
  }
}

// ============================================================
// UPDATE INVOICE (name / email)
// PUT /invoice
//
// Body: { invoiceId, name, email }
//
// Note this one puts invoiceId in the BODY, not the URL -- unlike
// every other call here. Left alone in case something depends on it.
//
// (The old section header above this function said "ADD PAYMENT".)
// ============================================================

export async function invoiceUpdate(data) {
  try {
    const response = await api.put(`${Api.request.URL}/invoice`, data);
    return response.data;
  } catch (error) {
    return toError(error, "Unable to update invoice.");
  }
}
export async function InvoiceSummaryStats(auctionId = null) {
  try {
    const url = auctionId
      ? `${Api.request.URL}/invoice/summary/${auctionId}`
      : `${Api.request.URL}/invoice/summary`;

    const response = await api.get(url);

    return response.data;
  } catch (error) {
    return toError(error, "Unable to load invoice summary.");
  }
}
